import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, getDocs, query, where, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getFunctionUrl } from '../../../../../utils/functionURLs';
import { reportCaughtError } from '../../../../../utils/clientErrorReporting';
import { safeOnSnapshot } from '../../../../../utils/firestoreSubscriptions';
import SubmitButton from '../../../formComponents/SubmitButton';
import { getCurrencyMeta } from '../../../../../utils/currency';
import { toMinorFromMajor } from '../../../../../utils/fees';
import {
  normalizeRsvpUid,
  resolveEffectiveRsvpRows,
  toRsvpRowFromSnapshot
} from '../../../../../utils/rsvpResolver';
import { remainingRefundableMinor, ticketAppliesToEvent } from '../../../../../utils/ticketCoverage';
import DestructiveActionDialog from '../../../../ui/DestructiveActionDialog';

// ── safe browser dialogs ──
const safeAlert = msg => typeof window !== 'undefined' ? window.alert(msg) : void 0;

// ── utils ──
function formatMoney(minor, currency = 'GBP') {
  const {
    decimals,
    symbol
  } = getCurrencyMeta(currency);
  const v = (Number(minor || 0) / Math.pow(10, decimals)).toFixed(decimals);
  return `${symbol}${v}`;
}
function RefundAmountControl({ ticket, onRefund, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pendingRefundAmount, setPendingRefundAmount] = useState(0);
  const remaining = remainingRefundableMinor(ticket);
  const submit = async (amountMinor) => {
    if (!amountMinor || amountMinor <= 0 || amountMinor > remaining) {
      safeAlert('Enter an amount no greater than the remaining refundable balance.');
      return;
    }
    setPendingRefundAmount(amountMinor);
  };
  const confirmRefund = async () => {
    const amountMinor = pendingRefundAmount;
    if (!amountMinor) return;
    setSubmitting(true);
    try {
      await onRefund(amountMinor);
      setOpen(false);
      setAmount('');
      setPendingRefundAmount(0);
    } catch (_) {
      // The caller presents the backend error and the form remains open for retry.
    } finally {
      setSubmitting(false);
    }
  };
  const confirmationAmount = formatMoney(pendingRefundAmount, ticket.currency);
  return <div style={{ display: 'grid', gap: 6, minWidth: 190 }}>
    <small className="text-muted">Paid {formatMoney(ticket.amountTotal, ticket.currency)} · refunded {formatMoney(ticket.refundedAmount, ticket.currency)} · available {formatMoney(remaining, ticket.currency)}</small>
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      <SubmitButton type="button" size="sm" variant="secondary" label="Full refund" disabled={disabled || submitting || remaining <= 0} onClick={() => submit(remaining)} />
      <SubmitButton type="button" size="sm" variant="ghost" label={open ? 'Cancel amount' : 'Choose amount'} disabled={disabled || submitting || remaining <= 0} onClick={() => setOpen(value => !value)} />
    </div>
    {open && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'end' }}>
      <label style={{ display: 'grid', gap: 3, flex: '1 1 110px' }}><small>Refund amount</small><input className="input" inputMode="decimal" value={amount} onChange={event => setAmount(event.target.value)} placeholder="e.g. 5.00" /></label>
      <SubmitButton type="button" size="sm" variant="secondary" label={submitting ? 'Refunding…' : 'Refund'} disabled={submitting} onClick={() => submit(toMinorFromMajor(amount, ticket.currency || 'GBP'))} />
    </div>}
    <DestructiveActionDialog
      isOpen={pendingRefundAmount > 0}
      title="Confirm refund"
      message={`Refund ${confirmationAmount}? Refunds cannot be reversed from Member Nest.`}
      confirmationText={confirmationAmount}
      confirmLabel={`Refund ${confirmationAmount}`}
      cancelLabel="Keep payment"
      busy={submitting}
      onClose={() => !submitting && setPendingRefundAmount(0)}
      onConfirm={confirmRefund}
    />
  </div>;
}
async function requestRefundHTTP({
  teamId,
  eventId,
  ticketDocId,
  amountMinor,
  note
}) {
  const user = getAuth().currentUser;
  const idToken = user && (await user.getIdToken());
  const url = getFunctionUrl('refundEventTicket');
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`
    },
    body: JSON.stringify({
      teamId,
      eventId,
      ticketDocId,
      amountMinor,
      note
    })
  });
  const raw = await resp.text();
  let data = null;
  try {
    data = raw ? JSON.parse(raw) : null;
  } catch (_) {}
  if (!resp.ok) {
    const message = data?.error || data?.message || `Refund failed (${resp.status}): ${raw?.slice(0, 200) || 'no body'}`;
    throw new Error(message);
  }
  return data ?? {
    ok: true,
    raw
  };
}

// Prefer displayName → "First Last" → email. Never expose UIDs in UI.
export function renderUserName(uid, udoc, fallback = 'Unknown member') {
  if (!udoc) return fallback;
  const dn = udoc.displayName && String(udoc.displayName).trim();
  if (dn) return dn;
  const fn = udoc.firstName && String(udoc.firstName).trim();
  const ln = udoc.lastName && String(udoc.lastName).trim();
  const full = [fn, ln].filter(Boolean).join(' ').trim();
  if (full) return full;
  if (udoc.email) return String(udoc.email).trim();
  return fallback;
}
const RSVP_BUCKET_ORDER = ['going', 'notGoing', 'noResponse'];
const RSVP_BUCKET_META = {
  all: {
    label: 'All',
    bg: '#e2e8f0',
    color: '#0f172a',
    border: '#cbd5e1'
  },
  going: {
    label: 'Going',
    bg: '#dcfce7',
    color: '#166534',
    border: '#86efac'
  },
  notGoing: {
    label: 'Not going',
    bg: '#fee2e2',
    color: '#991b1b',
    border: '#fecaca'
  },
  noResponse: {
    label: 'No response',
    bg: '#f1f5f9',
    color: '#334155',
    border: '#cbd5e1'
  }
};
function getRsvpBucket(status) {
  if (status === 'yes') return 'going';
  if (status === 'no') return 'notGoing';
  return 'noResponse';
}
export function resolveRsvpRowsForDisplay({
  rows,
  occurrenceId = null,
  isRecurring = false,
  rsvpSeriesResponsesEnabled = true
}) {
  return resolveEffectiveRsvpRows({
    rows,
    occurrenceId,
    isRecurring,
    seriesResponsesEnabled: rsvpSeriesResponsesEnabled
  });
}
export function mergeRsvpRows(rows, extraRows) {
  if (!Array.isArray(extraRows) || extraRows.length === 0) return rows || [];
  const byId = new Map();
  (rows || []).forEach(row => {
    if (row?.id) byId.set(row.id, row);
  });
  extraRows.filter(Boolean).forEach(row => {
    if (row?.id) byId.set(row.id, row);
  });
  return Array.from(byId.values());
}
export function buildRsvpAttendeeCountMismatch({
  rows,
  extraRsvpRows = [],
  occurrenceId = null,
  isRecurring = false,
  rsvpSeriesResponsesEnabled = true,
  displayedRows = null
}) {
  if (!occurrenceId) return null;
  const rowsForDisplay = mergeRsvpRows(rows, extraRsvpRows);
  const displayed = Array.isArray(displayedRows) ? displayedRows : resolveRsvpRowsForDisplay({
    rows: rowsForDisplay,
    occurrenceId,
    isRecurring,
    rsvpSeriesResponsesEnabled
  });
  const expected = resolveRsvpRowsForDisplay({
    rows: rowsForDisplay,
    occurrenceId,
    isRecurring: true,
    rsvpSeriesResponsesEnabled
  });
  const displayedCount = displayed.length;
  const expectedCount = expected.length;
  if (displayedCount === expectedCount) return null;
  return {
    displayedCount,
    expectedCount,
    occurrenceId,
    displayedGoingCount: displayed.filter(row => row?.status === 'yes').length,
    expectedGoingCount: expected.filter(row => row?.status === 'yes').length,
    rawRowCount: rowsForDisplay.length,
    isRecurring
  };
}
export default function AttendeesSection({
  db,
  teamId,
  eventId,
  attendanceMode,
  occurrenceId = null,
  isRecurring = false,
  readOnly = false,
  extraRsvpRows = [],
  rsvpSeriesResponsesEnabled = true,
  calendarSurface = 'unknown',
  ticketPaymentBasis = '',
  canManageRefunds = false,
}) {
  const [rows, setRows] = useState([]); // tickets OR rsvps
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [payoutMap, setPayoutMap] = useState({}); // ticketId -> 'Held' | 'Pending' | 'Paid out'
  const [userMap, setUserMap] = useState({}); // uid -> user doc data (name/email, etc.)
  const [refundReqMap, setRefundReqMap] = useState({}); // ticketId -> requests
  const [rsvpFilter, setRsvpFilter] = useState('all');
  const [groupByAttendance, setGroupByAttendance] = useState(true);
  const lastReportedMismatchRef = useRef('');
  const applyLocalRefund = (ticketId, amountMinor) => setRows(current => current.map(row => {
    if (row.id !== ticketId) return row;
    const refundedAmount = Math.min(Number(row.amountTotal || 0), Number(row.refundedAmount || 0) + Number(amountMinor || 0));
    return { ...row, refundedAmount, status: refundedAmount >= Number(row.amountTotal || 0) ? 'refunded' : 'partially_refunded' };
  }));
  const markRefundRequest = async (ticketId, userId, status) => {
    if (!teamId || !eventId || !ticketId || !userId) return;
    const ref = doc(db, 'teams', teamId, 'events', eventId, 'refundRequests', `${ticketId}:${userId}`);
    await setDoc(ref, {
      ticketId,
      userId,
      status,
      updatedAt: serverTimestamp()
    }, {
      merge: true
    });
  };
  useEffect(() => {
    if (!teamId || !eventId) return;
    let alive = true;
    let unsubscribe = null;
    const loadProfiles = async list => {
      try {
        const uids = Array.from(new Set(list.map(t => attendanceMode === 'rsvp' ? normalizeRsvpUid(t) : t.userId || t.memberId || t.id).filter(Boolean)));
        const nextMap = {};
        await Promise.all(uids.map(async uid => {
          try {
            const [userSnap, memberSnap] = await Promise.all([
              getDoc(doc(db, 'users', uid)).catch(() => null),
              getDoc(doc(db, 'teams', teamId, 'members', uid)).catch(() => null)
            ]);
            if (userSnap?.exists()) {
              nextMap[uid] = userSnap.data();
            } else if (memberSnap?.exists()) {
              nextMap[uid] = {
                ...memberSnap.data(),
                _profileMissing: true
              };
            } else {
              nextMap[uid] = {
                _profileMissing: true
              };
            }
          } catch (e) {
            nextMap[uid] = {
              _profileMissing: true
            };
          }
        }));
        if (alive) setUserMap(nextMap);
      } catch (e) {
        if (alive) setUserMap({});
      }
    };
    (async () => {
      setBusy(true);
      setLoadError('');
      try {
        let list = [];

        // ── 1) Load base rows depending on attendance mode ──
        if (attendanceMode === 'ticketed') {
          const tSnap = await getDocs(collection(db, `teams/${teamId}/events/${eventId}/tickets`));
          list = (tSnap.docs.map(d => ({
            id: d.id,
            ...d.data()
          })) || []
          // drop "pending" — not completed yet
          ).filter(t => t?.status !== 'pending').filter(t => ticketAppliesToEvent(t, { isRecurring, ticketPaymentBasis, occurrenceId }));

          // load refund requests from event-level collection
          try {
            const reqSnap = await getDocs(collection(db, `teams/${teamId}/events/${eventId}/refundRequests`));
            const reqs = {};
            reqSnap.forEach(r => {
              const data = r.data() || {};
              const tid = data.ticketId;
              if (!tid) return;
              reqs[tid] = reqs[tid] || [];
              reqs[tid].push({
                id: r.id,
                ...data
              });
            });
            if (alive) setRefundReqMap(reqs);
          } catch (_) {
            if (alive) setRefundReqMap({});
          }
        } else if (attendanceMode === 'rsvp') {
          unsubscribe = safeOnSnapshot(collection(db, `teams/${teamId}/events/${eventId}/rsvps`), rSnap => {
            const liveList = rSnap.docs.map(toRsvpRowFromSnapshot).filter(Boolean);
            if (!alive) return;
            setRows(liveList);
            setBusy(false);
            loadProfiles(liveList);
          }, () => {
            if (alive) {
              setBusy(false);
            }
          }, { label: 'event-attendees-rsvps', teamId, eventId });
          return;
        } else {
          // No tracking
          if (alive) {
            setRows([]);
            setUserMap({});
            setPayoutMap({});
            setRefundReqMap({});
          }
          return;
        }
        if (!alive) return;
        setRows(list);

        // ── 2) Resolve user names (best-effort) ──
        await loadProfiles(list);

        // ── 3) Payout glance only for ticketed events ──
        if (attendanceMode === 'ticketed' && canManageRefunds) {
          try {
            const ptQ = query(collection(db, 'pendingTransfers'), where('teamId', '==', teamId), where('eventId', '==', eventId));
            const ptSnap = await getDocs(ptQ);
            if (!alive) return;
            const map = {};
            ptSnap.forEach(d => {
              const r = d.data();
              const ticketId = r.ticketDocId;
              const status = r.status;
              const current = map[ticketId];
              const label = status === 'completed' ? 'Transferred' : status === 'pending' ? 'Transfer pending' : status === 'waiting' ? 'Held' : 'Held';
              const val = { label, readyAt: r.readyAt || null };
              const rank = {
                Transferred: 3,
                'Transfer pending': 2,
                Held: 1
              };
              map[ticketId] = !current || rank[val.label] > rank[current.label] ? val : current;
            });
            if (alive) setPayoutMap(map);
          } catch (e) {
            if (alive) setPayoutMap({});
          }
        } else {
          if (alive) {
            setPayoutMap({});
            setRefundReqMap({});
          }
        }
      } catch (error) {
        if (alive) {
          setRows([]);
          setUserMap({});
          setLoadError(error?.code === 'permission-denied'
            ? 'Ticket purchases exist, but your account cannot read this attendee list.'
            : 'The attendee list could not be loaded. Refresh and try again.');
        }
      } finally {
        if (alive) setBusy(false);
      }
    })();
    return () => {
      alive = false;
      if (unsubscribe) unsubscribe();
    };
  }, [db, teamId, eventId, attendanceMode, canManageRefunds, isRecurring, occurrenceId, ticketPaymentBasis]);
  useEffect(() => {
    setRsvpFilter('all');
    setGroupByAttendance(true);
  }, [eventId, attendanceMode]);
  const heading = attendanceMode === 'rsvp' ? 'RSVPs' : 'Attendees';
  const resolvedRsvpRows = useMemo(() => {
    if (attendanceMode !== 'rsvp') return [];
    const rowsForDisplay = mergeRsvpRows(rows, extraRsvpRows);
    return resolveRsvpRowsForDisplay({
      rows: rowsForDisplay,
      occurrenceId,
      isRecurring,
      rsvpSeriesResponsesEnabled
    });
  }, [attendanceMode, rows, extraRsvpRows, occurrenceId, isRecurring, rsvpSeriesResponsesEnabled]);
  const normalizedRsvpRows = useMemo(() => {
    if (attendanceMode !== 'rsvp') return [];
    return resolvedRsvpRows.map(r => {
      const uid = normalizeRsvpUid(r) || r._rsvpUid || r.id;
      const rowDisplayName = typeof r.displayName === 'string' && r.displayName.trim() !== '' ? r.displayName.trim() : '';
      const userLabel = renderUserName(uid, userMap[uid], userMap[uid]?._profileMissing ? 'Former member' : rowDisplayName || 'Unknown member');
      const bucket = getRsvpBucket(r.status);
      const updated = r.updatedAt && r.updatedAt.toDate ? r.updatedAt.toDate().toLocaleString() : '—';
      return {
        ...r,
        uid,
        userLabel,
        bucket,
        updated,
        sourceLabel: r._rsvpSourceLabel || (r._rsvpScope === 'occurrence' ? 'This date' : 'Series default')
      };
    }).sort((a, b) => a.userLabel.localeCompare(b.userLabel, undefined, {
      sensitivity: 'base'
    }));
  }, [attendanceMode, resolvedRsvpRows, userMap]);
  const rsvpCounts = useMemo(() => {
    const counts = {
      all: normalizedRsvpRows.length,
      going: 0,
      notGoing: 0,
      noResponse: 0
    };
    normalizedRsvpRows.forEach(row => {
      counts[row.bucket] += 1;
    });
    return counts;
  }, [normalizedRsvpRows]);
  const attendeeCountMismatch = useMemo(() => {
    if (attendanceMode !== 'rsvp') return null;
    return buildRsvpAttendeeCountMismatch({
      rows,
      extraRsvpRows,
      occurrenceId,
      isRecurring,
      rsvpSeriesResponsesEnabled,
      displayedRows: resolvedRsvpRows
    });
  }, [attendanceMode, rows, extraRsvpRows, occurrenceId, isRecurring, rsvpSeriesResponsesEnabled, resolvedRsvpRows]);
  useEffect(() => {
    if (!attendeeCountMismatch || busy) return;
    const reportKey = [
      calendarSurface,
      teamId,
      eventId,
      attendeeCountMismatch.occurrenceId,
      attendeeCountMismatch.displayedCount,
      attendeeCountMismatch.expectedCount,
      rsvpSeriesResponsesEnabled === false ? 'no-series' : 'series'
    ].join('|');
    if (lastReportedMismatchRef.current === reportKey) return;
    lastReportedMismatchRef.current = reportKey;
    reportCaughtError({
      name: 'CalendarAttendeeCountMismatch',
      code: 'calendar_attendee_count_mismatch',
      message: 'Calendar RSVP attendee count mismatch'
    }, {
      source: 'calendar.attendee_count_mismatch',
      severity: 'warning',
      context: {
        calendarSurface,
        teamId,
        eventId,
        occurrenceId: attendeeCountMismatch.occurrenceId,
        displayedCount: attendeeCountMismatch.displayedCount,
        expectedCount: attendeeCountMismatch.expectedCount,
        displayedGoingCount: attendeeCountMismatch.displayedGoingCount,
        expectedGoingCount: attendeeCountMismatch.expectedGoingCount,
        rawRowCount: attendeeCountMismatch.rawRowCount,
        isRecurring: attendeeCountMismatch.isRecurring,
        rsvpSeriesResponsesEnabled: rsvpSeriesResponsesEnabled !== false
      }
    });
  }, [attendeeCountMismatch, busy, calendarSurface, eventId, rsvpSeriesResponsesEnabled, teamId]);
  const filteredRsvpRows = useMemo(() => {
    if (rsvpFilter === 'all') return normalizedRsvpRows;
    return normalizedRsvpRows.filter(row => row.bucket === rsvpFilter);
  }, [normalizedRsvpRows, rsvpFilter]);
  const hasVisibleRows = attendanceMode === 'rsvp' ? normalizedRsvpRows.length > 0 : rows.length > 0;
  const normalizedTicketRows = useMemo(() => {
    if (attendanceMode !== 'ticketed') return [];
    return rows.map(row => {
      const uid = row.userId || row.memberId || row.id;
      return {
        ...row,
        uid,
        userLabel: renderUserName(uid, userMap[uid], userMap[uid]?._profileMissing ? 'Former member' : 'Unknown member'),
        quantity: Number(row.quantity || 1),
      };
    }).sort((a, b) => a.userLabel.localeCompare(b.userLabel, undefined, {
      sensitivity: 'base'
    }));
  }, [attendanceMode, rows, userMap]);
  const groupedRsvpRows = useMemo(() => RSVP_BUCKET_ORDER.map(bucket => ({
    bucket,
    rows: filteredRsvpRows.filter(row => row.bucket === bucket)
  })).filter(group => group.rows.length > 0), [filteredRsvpRows]);
  const renderRsvpRow = row => {
    const meta = RSVP_BUCKET_META[row.bucket] || RSVP_BUCKET_META.noResponse;
    return <div key={row.id} style={{
      border: '1px solid #e5e7eb',
      borderRadius: 10,
      padding: '10px 12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      flexWrap: 'wrap'
    }}>
        <div style={{
        fontWeight: 600
      }}>{row.userLabel}</div>
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap'
      }}>
          <span style={{
          padding: '2px 8px',
          borderRadius: 999,
          background: meta.bg,
          color: meta.color,
          border: `1px solid ${meta.border}`,
          fontSize: 12,
          fontWeight: 600
        }}>
            {meta.label}
          </span>
          <span className="text-muted small">{row.updated}</span>
          {row.sourceLabel && <span className="text-muted small">{row.sourceLabel}</span>}
        </div>
      </div>;
  };
  return <>
      <h3 style={{
      marginTop: 16
    }}>{heading}</h3>
      <div className="card">
        <div className="card-body">
          {busy ? 'Loading…' : loadError ? <div className="error-message" role="alert">{loadError}</div> : !hasVisibleRows ? attendanceMode === 'rsvp' ? 'No RSVPs yet.' : 'No tickets yet.' : attendanceMode === 'rsvp' ? <>
              <div className="text-muted small" style={{
            marginBottom: 8
          }}>
                {rsvpCounts.going} going, {rsvpCounts.notGoing} not going, {rsvpCounts.all} responses.
              </div>

              <div style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            marginBottom: 10
          }}>
                {['all', 'going', 'notGoing', 'noResponse'].map(bucket => <SubmitButton key={bucket} type="button" size="sm" variant={rsvpFilter === bucket ? 'secondary' : 'ghost'} label={`${RSVP_BUCKET_META[bucket].label} (${rsvpCounts[bucket] || 0})`} onClick={() => setRsvpFilter(bucket)} />)}
              </div>

              <div style={{
            marginBottom: 12
          }}>
                <SubmitButton type="button" size="sm" variant={groupByAttendance ? 'secondary' : 'ghost'} label={groupByAttendance ? 'Grouped by attendance' : 'Single list'} onClick={() => setGroupByAttendance(v => !v)} />
              </div>

              {filteredRsvpRows.length === 0 ? <div className="text-muted small" style={{
            marginBottom: 8
          }}>
                  No attendees match this filter.
                </div> : groupByAttendance ? <div style={{
            display: 'grid',
            gap: 10
          }}>
                  {groupedRsvpRows.map(group => {
              const meta = RSVP_BUCKET_META[group.bucket];
              return <div key={group.bucket} style={{
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                padding: 10,
                background: '#fff'
              }}>
                        <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 8
                }}>
                          <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}>
                            <span style={{
                      padding: '2px 8px',
                      borderRadius: 999,
                      background: meta.bg,
                      color: meta.color,
                      border: `1px solid ${meta.border}`,
                      fontSize: 12,
                      fontWeight: 600
                    }}>
                              {meta.label}
                            </span>
                          </div>
                          <span className="text-muted small">{group.rows.length}</span>
                        </div>
                        <div style={{
                  display: 'grid',
                  gap: 8
                }}>
                          {group.rows.map(row => renderRsvpRow(row))}
                        </div>
                      </div>;
            })}
                </div> : <div style={{
            display: 'grid',
            gap: 8
          }}>
                  {filteredRsvpRows.map(row => renderRsvpRow(row))}
                </div>}
            </> : readOnly ? <div style={{
          display: 'grid',
          gap: 8
        }}>
              {normalizedTicketRows.map(ticket => <div key={ticket.id} style={{
            border: '1px solid #e5e7eb',
            borderRadius: 10,
            padding: '10px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap'
          }}>
                  <div style={{
              fontWeight: 600
            }}>{ticket.userLabel}</div>
                  <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap'
            }}>
                    <span style={{
                padding: '2px 8px',
                borderRadius: 999,
                background: '#e2e8f0',
                color: '#0f172a',
                border: '1px solid #cbd5e1',
                fontSize: 12,
                fontWeight: 600
              }}>
                      {ticket.status || 'Unknown'}
                    </span>
                    {ticket.quantity > 1 && <span className="text-muted small">Qty {ticket.quantity}</span>}
                  </div>
                </div>)}
            </div> :
        // Ticketed event view (existing behaviour)
        <div className="table wrap">
              <div className="table-row table-head" style={{
            gridTemplateColumns: '2fr .6fr .9fr .9fr .9fr 1.2fr 1.3fr'
          }}>
                <div>User</div>
                <div>Qty</div>
                <div>Status</div>
                <div>Paid</div>
                <div>Payout</div>
                <div>Refund req</div>
                <div>Actions</div>
              </div>

              {rows.map(t => {
            const payout = payoutMap[t.id] || null;
            const canRefund = t.status === 'paid' || t.status === 'partially_refunded';
            const uid = t.userId || t.memberId || t.id;
            const userLabel = renderUserName(uid, userMap[uid], userMap[uid]?._profileMissing ? 'Former member' : 'Unknown member');
            const reqs = refundReqMap[t.id] || [];
            const pendingReq = reqs.find(r => (r.status || 'requested') === 'requested');
            const reqStatus = pendingReq ? 'Requested' : reqs.find(r => r.status === 'approved') ? 'Approved' : reqs.find(r => r.status === 'rejected') ? 'Rejected' : '—';
            return <div key={t.id} className="table-row" style={{
              gridTemplateColumns: '2fr .6fr .9fr .9fr .9fr 1.2fr 1.3fr'
            }}>
                    <div>{userLabel}</div>
                    <div>{t.quantity || 1}</div>
                    <div>{t.status}</div>
                    <div>{formatMoney(t.amountTotal, t.currency)}</div>
                    <div>{payout ? <>
                      <div>{payout.label}</div>
                      {payout.readyAt && payout.label !== 'Transferred' && <small className="text-muted">Eligible {payout.readyAt?.toDate ? payout.readyAt.toDate().toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : new Date(payout.readyAt).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}</small>}
                    </> : canManageRefunds ? 'Held' : '—'}</div>
                    <div>{reqStatus}</div>

                    <div style={{
                display: 'flex',
                gap: 8,
                flexWrap: 'wrap'
              }}>
                      {canManageRefunds && pendingReq && <>
                          <RefundAmountControl ticket={t} onRefund={async amountMinor => {
                            try {
                              await requestRefundHTTP({ teamId, eventId, ticketDocId: t.id, amountMinor, note: 'Admin-approved refund request' });
                              applyLocalRefund(t.id, amountMinor);
                              await markRefundRequest(t.id, pendingReq.userId || pendingReq.id, 'approved');
                              safeAlert('Refund approved.');
                              setRefundReqMap(prev => ({ ...prev, [t.id]: (prev[t.id] || []).map(r => r.id === pendingReq.id ? { ...r, status: 'approved' } : r) }));
                            } catch (e) { safeAlert(e.message || 'Refund failed'); throw e; }
                          }} />
                          <SubmitButton type="button" size="sm" variant="ghost" label="Reject" onClick={async () => {
                    await markRefundRequest(t.id, pendingReq.userId || pendingReq.id, 'rejected');
                    setRefundReqMap(prev => ({
                      ...prev,
                      [t.id]: (prev[t.id] || []).map(r => r.id === (pendingReq.userId || pendingReq.id) ? {
                        ...r,
                        status: 'rejected'
                      } : r)
                    }));
                  }} />
                        </>}
                      {canManageRefunds && canRefund && !pendingReq && <RefundAmountControl ticket={t} onRefund={async amountMinor => {
                        try {
                          await requestRefundHTTP({ teamId, eventId, ticketDocId: t.id, amountMinor, note: 'Admin refund' });
                          applyLocalRefund(t.id, amountMinor);
                          safeAlert('Refund submitted.');
                        } catch (e) { safeAlert(e.message || 'Refund failed'); throw e; }
                      }} />}
                    </div>
                  </div>;
          })}
            </div>}
        </div>
      </div>
    </>;
}
